import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useApolloClient } from "@apollo/client/react";
import { heartBeat } from "../../networking/apiMethods"; // non-strict import
import { useOverlayControl } from "./AppOverlayController";

const INITIAL_INTERVAL_S = 1;
const MAX_INTERVAL_S = 30;
const HEALTHY_POLL_INTERVAL_S = 10;

interface ConnectivityInfo {
  countdown: number;
  retryNow: () => void;
}

const ConnectivityContext = createContext<ConnectivityInfo>({ countdown: 0, retryNow: noop });

/** Seconds until the next heartbeat retry (0 when healthy), and a function to force an immediate retry. */
export const useConnectivityInfo = (): ConnectivityInfo => useContext(ConnectivityContext);

/**
 * Always-mounted component that proactively polls /_heartbeat to detect
 * connectivity loss and recovery. Starts with a 1s retry on failure and
 * backs off to 30s. Exposes a countdown via context for the overlay UI.
 * On recovery, triggers a global Apollo refetch so all active queries reload.
 */
export const ConnectivityMonitor: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { reportConnectivityLost, reportConnectivityRestored, activeOverlay } = useOverlayControl();
  const client = useApolloClient();
  const [countdown, setCountdown] = useState(0);
  const intervalRef = useRef(INITIAL_INTERVAL_S);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const tickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isDown = activeOverlay === "connectivity_lost";

  // scheduleHeartbeat is created with useCallback so it's stable, but the timer callback
  // (inside setTimeout) captures it at the time it's scheduled. scheduleRef lets the timer
  // call the current version of the function without having to reschedule every heartbeat.
  // reportLostRef / reportRestoredRef follow the same pattern for the overlay callbacks.
  const reportLostRef = useRef(reportConnectivityLost);
  const reportRestoredRef = useRef(reportConnectivityRestored);
  const scheduleRef = useRef<(delaySec: number) => void>(noop);

  useEffect(() => {
    reportLostRef.current = reportConnectivityLost;
  }, [reportConnectivityLost]);
  useEffect(() => {
    reportRestoredRef.current = reportConnectivityRestored;
  }, [reportConnectivityRestored]);

  const clearTimers = useCallback((): void => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (tickRef.current !== null) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
  }, []);

  const scheduleHeartbeat = useCallback(
    (delaySec: number): void => {
      clearTimers();
      setCountdown(delaySec);

      tickRef.current = setInterval(() => {
        setCountdown((prev) => (prev > 1 ? prev - 1 : prev));
      }, 1_000);

      timerRef.current = setTimeout(() => {
        heartBeat()
          .then((response) => {
            if (response.data === "ok\n") {
              reportRestoredRef.current();
              intervalRef.current = INITIAL_INTERVAL_S;
              scheduleRef.current(HEALTHY_POLL_INTERVAL_S);
            } else {
              reportLostRef.current();
              intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL_S);
              scheduleRef.current(intervalRef.current);
            }
          })
          .catch(() => {
            reportLostRef.current();
            intervalRef.current = Math.min(intervalRef.current * 2, MAX_INTERVAL_S);
            scheduleRef.current(intervalRef.current);
          });
      }, delaySec * 1_000);
    },
    [clearTimers]
  );

  useEffect(() => {
    scheduleRef.current = scheduleHeartbeat;
  }, [scheduleHeartbeat]);

  const retryNow = useCallback((): void => {
    intervalRef.current = INITIAL_INTERVAL_S;
    scheduleHeartbeat(0);
  }, [scheduleHeartbeat]);

  // Bootstrap: start polling on mount — setCountdown inside scheduleHeartbeat is valid here
  // because this effect synchronizes with the external heartbeat timer system.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- timer-based external sync
    scheduleHeartbeat(HEALTHY_POLL_INTERVAL_S);
    return clearTimers;
  }, [scheduleHeartbeat, clearTimers]);

  // When overlay transitions to connectivity_lost (e.g. from GraphQL error), reset backoff and start fast retries
  useEffect(() => {
    if (isDown) {
      intervalRef.current = INITIAL_INTERVAL_S;
      // eslint-disable-next-line react-hooks/set-state-in-effect -- timer-based external sync
      scheduleHeartbeat(INITIAL_INTERVAL_S);
    }
  }, [isDown, scheduleHeartbeat]);

  // When connectivity is restored, refetch all active Apollo queries so data reloads automatically
  const prevIsDown = useRef(isDown);
  useEffect(() => {
    if (prevIsDown.current && !isDown) {
      void client.refetchQueries({ include: "active" });
    }
    prevIsDown.current = isDown;
  }, [isDown, client]);

  const countdownValue = isDown ? countdown : 0;
  const info = useMemo<ConnectivityInfo>(() => ({ countdown: countdownValue, retryNow }), [countdownValue, retryNow]);

  return <ConnectivityContext.Provider value={info}>{children}</ConnectivityContext.Provider>;
};

function noop(): void {
  // placeholder for scheduleRef initial value
}
